import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { useAppSelector } from '../../hooks/redux';
import { getAssetSummary, getPlantById, AssetSummaryItem, AlertItem, fetchAllAssetSummaryPages } from '../../Services/serviceURL';
// Using available images from the Images directory
import Close from '../../Images/CloseIcon.svg';
import 'leaflet/dist/leaflet.css';
import './MapView.css';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Create custom colored marker icons
const createColoredMarker = (color: string) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      background-color: ${color};
      width: 20px;
      height: 20px;
      border-radius: 50%;
      border: 2px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
};

// Create cluster marker icon with count
const createClusterMarker = (count: number, totalAlerts: number = 0) => {
  const size = count > 99 ? 50 : count > 9 ? 40 : 30;
  // Color based on total alerts in cluster
  const color = totalAlerts > 10 ? '#dc2626' : totalAlerts > 0 ? '#f59e0b' : '#10b981';
  
  return L.divIcon({
    className: 'custom-cluster-marker',
    html: `<div style="
      background-color: ${color};
      width: ${size}px;
      height: ${size}px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 4px 8px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: ${count > 99 ? '12px' : '14px'};
      cursor: pointer;
    ">${count}</div>`,
    iconSize: [size, size],
    iconAnchor: [size/2, size/2],
  });
};

// Define marker colors based on alerts count
const getMarkerColor = (alertsCount: number): string => {
  // Color based on alert count from the API
  if (alertsCount >= 3) return '#dc2626'; // Red for high alerts
  if (alertsCount >= 1) return '#f59e0b'; // Yellow/Orange for any alerts  
  return '#10b981'; // Green for no alerts
};

// Simple utility function for time conversion
const convertTimeToLocal = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    return dateString || '';
  }
};



// Interface for Map Asset (simplified, now directly from API)
interface MapViewAsset {
  id: string;
  assetId: string;
  name: string;
  type: string;
  status: string;
  alertsCount: number;
  latitude?: string;
  longitude?: string;
  alerts: AlertItem[];
  lastCommunication: string;
  firstCommunication:string;
  hardwareHealth: string | null;
  assetHealth: string | null;
  gatewayDeviceId?: string;
  simNumber?: string;
}

// Interface for marker clusters (now asset-based instead of plant-based)
interface MarkerCluster {
  id: string;
  lat: number;
  lng: number;
  count: number;
  assets: MapViewAsset[];
  displayName: string;
  customerId: string;
  customerName: string;
  siteId?: string;
  siteName?: string;
  plantId?: string;
  plantName?: string;
  isCluster: boolean;
  totalAlerts: number;
}

// Component to handle map events
const MapEvents: React.FC<{ onZoomChange: (zoom: number) => void }> = ({ onZoomChange }) => {
  useMapEvents({
    zoomend: (e) => {
      const zoom = e.target.getZoom();
      onZoomChange(zoom);
    },
  });
  return null;
};

// Removed AssetMapItem interface - now using MapViewAsset from API

interface MapViewProps {
  selectedCustomerId?: string;
  selectedSiteId?: string; 
  selectedPlantId?: string;
}

const MapView: React.FC<MapViewProps> = ({ 
  selectedCustomerId, 
  selectedSiteId, 
  selectedPlantId 
}) => {

  // State for map data and clustering
  const [mapViewAssets, setMapViewAssets] = useState<MapViewAsset[]>([]);
  const [clusters, setClusters] = useState<MarkerCluster[]>([]);
  const [mapZoom, setMapZoom] = useState<number>(4);
  const [loading, setLoading] = useState<boolean>(false);

  const [selectedMarker, setSelectedMarker] = useState<MarkerCluster | null>(null);

  // Function to convert AssetSummaryItem to MapViewAsset (including plant coordinates when asset coordinates are missing)
  const convertToMapViewAsset = (assetItem: AssetSummaryItem, plantCoordinates?: { latitude: string; longitude: string }): MapViewAsset | null => {
    // Use asset coordinates if available, otherwise use plant coordinates
    let latitude = assetItem.latitude;
    let longitude = assetItem.longitude;
    
    // If asset coordinates are missing or empty, use plant coordinates
    if ((!latitude || latitude === 'null' || latitude === '' || latitude === null) && plantCoordinates?.latitude) {
      latitude = plantCoordinates.latitude;
    }
    
    if ((!longitude || longitude === 'null' || longitude === '' || longitude === null) && plantCoordinates?.longitude) {
      longitude = plantCoordinates.longitude;
    }
    
    // Skip assets that still don't have valid coordinates after checking plant coordinates
    if (!latitude || !longitude || 
        latitude === 'null' || longitude === 'null' ||
        latitude === null || longitude === null ||
        latitude === '' || longitude === '') {
      return null;
    }

    const alertsCount = parseInt(assetItem.alertsCount) || assetItem.alerts?.length || 0;

    return {
      id: assetItem.id,
      assetId: assetItem.assetId,
      name: assetItem.assetName,
      type: assetItem.assetType,
      status: assetItem.status,
      alertsCount: alertsCount,
      latitude: latitude,
      longitude: longitude,
      alerts: assetItem.alerts,
      lastCommunication: assetItem.lastCommunication,
      firstCommunication:assetItem.firstCommunication,
      hardwareHealth: assetItem.hardwareHealth,
      assetHealth: assetItem.assetHealth,
      gatewayDeviceId: assetItem.gatewayDeviceId,
      simNumber: assetItem.simNumber
    };
  };



  // Function to fetch plant coordinates by plant ID
  const fetchPlantCoordinates = async (plantId: string): Promise<{ latitude: string; longitude: string } | null> => {
    try {
      const response = await getPlantById(plantId);
      
      if (response.data && response.data.Success && response.data.Data) {
        const plantData = response.data.Data;
        
        // Handle the actual API response structure you showed
        if (plantData.latitude && plantData.longitude) {
          return {
            latitude: plantData.latitude,
            longitude: plantData.longitude
          };
        }
        
        // Also check for capitalized field names as fallback
        if (plantData.Latitude && plantData.Longitude) {
          return {
            latitude: plantData.Latitude,
            longitude: plantData.Longitude
          };
        }
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  // Function to fetch assets for map view using Asset Summary API
  const fetchAssetsForMap = async () => {

    try {
      setLoading(true);

      // Call the Asset Summary API with the selected filters (fetch all pages for map)
      const assetSummaryItems: AssetSummaryItem[] = await fetchAllAssetSummaryPages(
        selectedCustomerId,
        selectedSiteId,
        selectedPlantId
      );

      if (assetSummaryItems.length > 0) {

        // Group assets by plant ID to minimize API calls
        const assetsByPlant = new Map<string, AssetSummaryItem[]>();
        assetSummaryItems.forEach(asset => {
          if (!assetsByPlant.has(asset.plantId)) {
            assetsByPlant.set(asset.plantId, []);
          }
          assetsByPlant.get(asset.plantId)!.push(asset);
        });

        // Fetch plant coordinates for each unique plant and convert assets
        const validAssets: MapViewAsset[] = [];
        
        const plantEntries = Array.from(assetsByPlant.entries());
        for (const [plantId, assets] of plantEntries) {
          // Fetch plant coordinates once per plant
          const plantCoordinates = await fetchPlantCoordinates(plantId);

          // Convert all assets for this plant
          for (const asset of assets) {
            const mapViewAsset = convertToMapViewAsset(asset, plantCoordinates || undefined);
            if (mapViewAsset) {
              validAssets.push(mapViewAsset);
            }
          }
        }

        setMapViewAssets(validAssets);
      } else {
        setMapViewAssets([]);
      }
    } catch (error) {
      setMapViewAssets([]);
    } finally {
      setLoading(false);
    }
  };


  
  // Function to calculate distance between two points (in degrees)
  const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    return Math.sqrt(Math.pow(lat1 - lat2, 2) + Math.pow(lng1 - lng2, 2));
  };

  // Function to create clusters based on zoom level
  const createClusters = (assets: MapViewAsset[], zoom: number): MarkerCluster[] => {
    if (assets.length === 0) return [];

    // Adjust clustering distance based on zoom level
    // Higher zoom = less clustering (smaller distance threshold)
    const clusterDistance = zoom > 10 ? 0.01 : zoom > 7 ? 0.05 : zoom > 5 ? 0.1 : 0.3;
    
    const clusters: MarkerCluster[] = [];
    const processed: boolean[] = new Array(assets.length).fill(false);

    assets.forEach((asset, index) => {
      if (processed[index]) return;

      const lat = parseFloat(asset.latitude || '0');
      const lng = parseFloat(asset.longitude || '0');
      
      if (isNaN(lat) || isNaN(lng)) {
        return;
      }

      const clusterAssets: MapViewAsset[] = [asset];
      processed[index] = true;

      // Find nearby assets to cluster
      assets.forEach((otherAsset, otherIndex) => {
        if (processed[otherIndex] || index === otherIndex) return;

        const otherLat = parseFloat(otherAsset.latitude || '0');
        const otherLng = parseFloat(otherAsset.longitude || '0');
        
        if (isNaN(otherLat) || isNaN(otherLng)) return;

        const distance = calculateDistance(lat, lng, otherLat, otherLng);

        if (distance < clusterDistance) {
          clusterAssets.push(otherAsset);
          processed[otherIndex] = true;
        }
      });

      const totalAlerts = clusterAssets.reduce((sum, asset) => sum + (asset.alertsCount || 0), 0);

      // Create cluster or single asset marker
      if (clusterAssets.length > 1) {
        // Calculate center point of cluster
        const avgLat = clusterAssets.reduce((sum, a) => sum + parseFloat(a.latitude || '0'), 0) / clusterAssets.length;
        const avgLng = clusterAssets.reduce((sum, a) => sum + parseFloat(a.longitude || '0'), 0) / clusterAssets.length;

        // Use the first asset's location info for cluster details
        const firstAsset = clusterAssets[0];
        
        clusters.push({
          id: `cluster-${clusters.length}`,
          lat: avgLat,
          lng: avgLng,
          count: clusterAssets.length,
          assets: clusterAssets,
          displayName: `${clusterAssets.length} Assets`,
          customerId: firstAsset.id, // Using asset id as fallback
          customerName: 'Multiple Assets',
          totalAlerts,
          isCluster: true
        });
      } else {
        // Single asset - also show as cluster with count "1"
        clusters.push({
          id: `asset-${asset.assetId}`,
          lat,
          lng,
          count: 1,
          assets: [asset],
          displayName: asset.name,
          customerId: asset.id,
          customerName: asset.name,
          totalAlerts: asset.alertsCount,
          isCluster: true // Changed to true to always show cluster marker with count
        });
      }
    });

    return clusters;
  };

  // Fetch asset data when component mounts or filters change
  useEffect(() => {
    fetchAssetsForMap();
  }, [selectedCustomerId, selectedSiteId, selectedPlantId]);

  // Create clusters when data or zoom changes
  useEffect(() => {
    if (mapViewAssets.length > 0) {
      const newClusters = createClusters(mapViewAssets, mapZoom);
      setClusters(newClusters);
    } else {
      setClusters([]);
    }
  }, [mapViewAssets, mapZoom]);

  // Calculate map center from clusters
  const getMapCenter = (): [number, number] => {
    if (clusters.length === 0) return [39.8283, -98.5795]; // Default to center of USA
    
    const latSum = clusters.reduce((sum, cluster) => sum + cluster.lat, 0);
    const lngSum = clusters.reduce((sum, cluster) => sum + cluster.lng, 0);
    
    return [latSum / clusters.length, lngSum / clusters.length];
  };

  // Calculate appropriate zoom level
  const getMapZoom = (): number => {
    if (clusters.length === 0) return 4;
    if (clusters.length === 1) return 8;
    return 4; // Default zoom for multiple clusters across USA
  };

  // Handle marker click
  const handleMarkerClick = (cluster: MarkerCluster) => {
    setSelectedMarker(cluster);
  };

  // Handle info window close
  const handleInfoWindowClose = () => {
    setSelectedMarker(null);
  };

  return (
    <div className="map-view-container">
      <div style={{ display: 'flex', width: '100%' }}>
        <div className='map-card' style={{ border: '1px solid #ddd', padding: '5px', borderRadius: '8px' }}>
          {loading ? (
            <div style={{ height: 500, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
              <p style={{ color: '#fff', marginTop: '16px' }}>Loading plants...</p>
            </div>
          ) : clusters.length === 0 ? (
            <div style={{ height: 500, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📍</div>
              <p style={{ color: '#fff', marginTop: '16px' }}>No plants with valid coordinates found</p>
              <p style={{ color: '#94a3b8', marginTop: '8px', fontSize: '14px' }}>Plants need latitude and longitude data to appear on the map</p>
            </div>
          ) : (
            <MapContainer
              center={getMapCenter()}
              zoom={getMapZoom()}
              style={{ 
                height: '500px', 
                width: '100%',
                borderRadius: '8px' 
              }}
            >
              <MapEvents onZoomChange={setMapZoom} />
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {clusters?.map((cluster) => {
                return (
                  <Marker
                    key={cluster.id}
                    position={[cluster.lat, cluster.lng]}
                    icon={cluster.isCluster 
                      ? createClusterMarker(cluster.count, cluster.totalAlerts)
                      : createColoredMarker(cluster.totalAlerts > 10 ? '#dc2626' : cluster.totalAlerts > 0 ? '#f59e0b' : '#10b981')
                    }
                    eventHandlers={{
                      click: () => handleMarkerClick(cluster),
                    }}
                  >
                    <Popup>
                      <div style={{ minWidth: '250px', maxWidth: '300px' }}>
                        {cluster.isCluster ? (
                          <div>
                            <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>
                              {cluster.plantName} ({cluster.count} assets)
                            </h4>
                            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                              {cluster.assets.map((asset, index) => (
                                <div 
                                  key={asset.id}
                                  style={{ 
                                    padding: '6px 0',
                                    borderBottom: index < cluster.assets.length - 1 ? '1px solid #eee' : 'none'
                                  }}
                                >
                                  <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#1f2937' }}>
                                    {asset.name}
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                    Alerts: <span style={{ 
                                      color: asset.alertsCount > 0 ? '#dc2626' : '#10b981',
                                      fontWeight: 'bold' 
                                    }}>
                                      {asset.alertsCount || 0}
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div style={{ marginTop: '10px', fontSize: '12px', color: '#666', borderTop: '1px solid #eee', paddingTop: '6px' }}>
                              Total Alerts: <span style={{ color: cluster.totalAlerts > 0 ? '#dc2626' : '#10b981', fontWeight: 'bold' }}>
                                {cluster.totalAlerts}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <h4 style={{ margin: '0 0 8px 0', color: '#333' }}>
                              {cluster.plantName}
                            </h4>
                            <div style={{ fontSize: '14px', marginBottom: '8px' }}>
                              <strong>Assets:</strong> {cluster.count}
                            </div>
                            <div style={{ fontSize: '14px', marginBottom: '8px' }}>
                              <strong>Total Alerts:</strong> <span style={{ 
                                color: cluster.totalAlerts > 0 ? '#dc2626' : '#10b981',
                                fontWeight: 'bold' 
                              }}>
                                {cluster.totalAlerts}
                              </span>
                            </div>
                            <div style={{ fontSize: '11px', color: '#666', marginTop: '6px' }}>
                              Click marker for details
                            </div>
                          </div>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          )}
        </div>
        
        {selectedMarker && (
          <div className='marker-detail-container'>
            <div className='head-container'>
              <p className='wellname'>{selectedMarker.displayName}</p>
              <img src={Close} alt='close' style={{ cursor: 'pointer' }} onClick={handleInfoWindowClose} />
            </div>

            <hr />

            <div className='marker-info-container'>
              <span className='api-text'>
                Location: <span className='api-value'>{selectedMarker.lat.toFixed(6)}, {selectedMarker.lng.toFixed(6)}</span>
              </span>
              {selectedMarker.customerName && (
                <span className='api-text'>
                  Customer: <span className='api-value'>{selectedMarker.customerName}</span>
                </span>
              )}
              {selectedMarker.siteName && (
                <span className='api-text'>
                  Site: <span className='api-value'>{selectedMarker.siteName}</span>
                </span>
              )}
              {selectedMarker.plantName && (
                <span className='api-text'>
                  Plant: <span className='api-value'>{selectedMarker.plantName}</span>
                </span>
              )}
            </div>

            <div className='marker-table'>
              <div className='marker-table-column'>
                <div>{selectedMarker.isCluster ? 'Clustered Assets' : 'Asset Name'}</div>
                <div>{selectedMarker.displayName}</div>
              </div>

              <div className='marker-table-column'>
                <div>Total Assets</div>
                <div>{selectedMarker.count}</div>
              </div>

              <div className='marker-table-column'>
                <div>Total Alerts</div>
                <div style={{ 
                  color: selectedMarker.totalAlerts > 0 ? '#dc2626' : '#10b981',
                  fontWeight: 'bold'
                }}>
                  {selectedMarker.totalAlerts}
                </div>
              </div>

              <div className='marker-table-column'>
                <div>Asset Details</div>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {selectedMarker.assets.map((asset, index) => (
                    <div key={asset.assetId} style={{ 
                      padding: '8px 0', 
                      borderBottom: index < selectedMarker.assets.length - 1 ? '1px solid #444' : 'none',
                      fontSize: '12px'
                    }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{asset.name}</div>
                      <div style={{ marginBottom: '2px' }}>
                        <span style={{ color: '#94a3b8' }}>Type: </span>
                        <span style={{ color: '#fff' }}>{asset.type}</span>
                      </div>
                      <div style={{ marginBottom: '2px' }}>
                        <span style={{ color: '#94a3b8' }}>Status: </span>
                        <span style={{ 
                          color: asset.status === 'Online' ? '#10b981' : asset.status === 'Offline' ? '#dc2626' : '#f59e0b' 
                        }}>
                          {asset.status}
                        </span>
                      </div>
                      <div style={{ marginBottom: '2px' }}>
                        <span style={{ color: '#94a3b8' }}>Alerts: </span>
                        <span style={{ color: asset.alertsCount > 0 ? '#dc2626' : '#10b981', fontWeight: 'bold' }}>
                          {asset.alertsCount || 0}
                        </span>
                      </div>
                      {asset.lastCommunication && (
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                          Last Comm: {convertTimeToLocal(asset.lastCommunication)}
                        </div>
                      )}
                      {asset.firstCommunication && (
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                          First Comm: {convertTimeToLocal(asset.firstCommunication)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MapView;